import { useEffect, useMemo, useRef, useState } from 'react';
import { api, fetchBacklog } from '../api';
import { useAppData } from '../AppData';
import { useAuth } from '../auth/AuthProvider';
import { BacklogTask, TIPOS, URGENCIAS, DIFICULTADES, TITULO_MAX } from '../domain/types';
import { nameByEmail, errMsg, truncate } from '../domain/util';
import AjusteInput from '../components/AjusteInput';
import CopyButton from '../components/CopyButton';

const today = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  titulo: '',
  description: '',
  enlace_documentos: '',
  solicitante: '',
  direccion: '',
  proyecto: '',
  categoria: '',
  pieza: '',
  sede: '',
  tipo: 'Ordinaria',
  urgencia: 'media',
  dificultad: '',
  horas_estimadas: '',
  assigned_to: '',
  fecha_solicitud: '',
  start_date: '',
  deadline: '',
  ajuste: '',
};

const fresh = (email: string) => ({ ...EMPTY, assigned_to: email, fecha_solicitud: today() });

/** Segmented control para enums cortos (más rápido que un select). */
function Seg({
  value,
  options,
  onChange,
  colorize,
}: {
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
  colorize?: boolean;
}) {
  return (
    <div className="segmented" role="radiogroup">
      {options.map((o) => (
        <button
          type="button"
          key={o}
          role="radio"
          aria-checked={value === o}
          className={`${value === o ? 'on' : ''} ${colorize ? `urg-${o}` : ''}`}
          onClick={() => onChange(o)}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

export default function NuevaTarea() {
  const { dimensions, users } = useAppData();
  const { email } = useAuth();
  const [form, setForm] = useState<Record<string, any>>(() => fresh(email));
  const [details, setDetails] = useState(() => localStorage.getItem('nt.details') === '1');
  const [keepGoing, setKeepGoing] = useState(() => localStorage.getItem('nt.keep') === '1');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [err, setErr] = useState('');
  const [errField, setErrField] = useState('');
  const [tareas, setTareas] = useState<BacklogTask[]>([]);
  const tituloRef = useRef<HTMLInputElement>(null);
  const descRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    fetchBacklog('admin', email).then(setTareas).catch(() => {});
  }, [email]);

  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));
  const piezas = useMemo(
    () => (dimensions.pieza || []).filter((p) => !form.categoria || p.categoria === form.categoria),
    [dimensions.pieza, form.categoria]
  );
  // proyecto -> dirección asociada (la dirección queda atada al proyecto)
  const projDireccion = useMemo(
    () => new Map((dimensions.proyecto || []).map((p) => [p.name, p.direccion_asociada || ''])),
    [dimensions.proyecto]
  );
  const setProyecto = (name: string) =>
    setForm((f) => ({ ...f, proyecto: name, direccion: name ? projDireccion.get(name) || '' : f.direccion }));

  const toggleDetails = () => setDetails((d) => { localStorage.setItem('nt.details', d ? '0' : '1'); return !d; });
  const toggleKeep = () => setKeepGoing((k) => { localStorage.setItem('nt.keep', k ? '0' : '1'); return !k; });

  const submit = async () => {
    setErr('');
    setErrField('');
    setToast('');
    if (!form.titulo.trim()) { setErr('El título es obligatorio.'); setErrField('titulo'); tituloRef.current?.focus(); return; }
    if (!form.description.trim()) { setErr('La descripción es obligatoria.'); setErrField('description'); descRef.current?.focus(); return; }
    if (!form.categoria) { setErr('La categoría es obligatoria.'); setErrField('categoria'); return; }
    if (!form.deadline) { setErr('La fecha de entrega solicitada es obligatoria.'); setErrField('deadline'); return; }

    setSaving(true);
    try {
      const { solicitante, ...rest } = form;
      const [kind, ...nameParts] = String(solicitante || '').split('::');
      const name = nameParts.join('::');
      const payload: Record<string, any> = { ...rest, espacio: '', cliente_interno: '' };
      if (kind === 'espacio') payload.espacio = name;
      if (kind === 'cliente_interno') payload.cliente_interno = name;

      const res = await api.createTask(payload);
      void fetchBacklog('admin', email).then(setTareas).catch(() => {});
      if (keepGoing) {
        // Carga en lote: mantiene contexto (asignado, solicitante, categoría), limpia el resto y refocusea.
        setForm((f) => ({ ...fresh(f.assigned_to), solicitante: f.solicitante, categoria: f.categoria }));
        setToast(`Tarea creada · notificado a ${nameByEmail(payload.assigned_to, users)}`);
        setTimeout(() => setToast(''), 3000);
        tituloRef.current?.focus();
      } else {
        setForm(fresh(form.assigned_to));
        setToast(`Tarea creada (ID ${res.id})`);
        setTimeout(() => setToast(''), 3000);
      }
    } catch (e) {
      setErr(errMsg(e));
    }
    setSaving(false);
  };

  const dimSelect = (k: string, label: string, opts: { id: string; name: string }[]) => (
    <div>
      <label>{label}</label>
      <select value={form[k]} onChange={(e) => set(k, e.target.value)}>
        <option value="">—</option>
        {opts.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
      </select>
    </div>
  );

  return (
    <div className="grid" style={{ maxWidth: 560, width: '100%', margin: '0 auto' }}>
      <div>
        {/* Se va escribiendo con el título; sin título, el encabezado genérico. */}
        <h1 style={{ marginBottom: 2 }}>{truncate(form.titulo, 70) || 'Nueva tarea'}</h1>
        <span className="muted" style={{ fontSize: 13 }}>Completá los datos del pedido. Los campos con <span className="req">*</span> son obligatorios.</span>
      </div>
      <div className="panel grid" style={{ gap: 'var(--sp-5)' }}>
        {/* Título */}
        <div className={errField === 'titulo' ? 'field-error' : ''}>
          <label>Título <span className="req">*</span></label>
          <input
            ref={tituloRef}
            type="text"
            value={form.titulo}
            maxLength={TITULO_MAX}
            placeholder="Un nombre corto para identificar la tarea"
            autoFocus
            onChange={(e) => set('titulo', e.target.value)}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit(); }}
          />
          <span className="hint">{form.titulo.length}/{TITULO_MAX} caracteres.</span>
        </div>

        {/* Descripción. El CopyButton va suelto entre el label y el textarea: ver CopyButton.tsx. */}
        <div className={`field-copy ${errField === 'description' ? 'field-error' : ''}`}>
          <label>Descripción <span className="req">*</span></label>
          <CopyButton text={form.description} />
          <textarea
            ref={descRef}
            rows={4}
            value={form.description}
            placeholder="¿Qué hay que hacer? Contexto, links, entregables…"
            onChange={(e) => set('description', e.target.value)}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit(); }}
          />
        </div>

        {/* Enlace a documentos */}
        <div>
          <label>Enlace de documentos</label>
          <input
            type="url"
            value={form.enlace_documentos}
            placeholder="https://drive.google.com/…"
            onChange={(e) => set('enlace_documentos', e.target.value)}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit(); }}
          />
          <span className="hint">Opcional: dónde viven los documentos de la tarea.</span>
        </div>

        {/* ¿Para quién? */}
        <div className="form-section">Para quién</div>
        <div>
          <label>Solicitante</label>
          <select value={form.solicitante} onChange={(e) => set('solicitante', e.target.value)}>
            <option value="">—</option>
            <optgroup label="Espacios">
              {(dimensions.espacio || []).map((d) => <option key={d.id} value={`espacio::${d.name}`}>{d.name}</option>)}
            </optgroup>
            <optgroup label="Clientes internos">
              {(dimensions.cliente_interno || []).map((d) => <option key={d.id} value={`cliente_interno::${d.name}`}>{d.name}</option>)}
            </optgroup>
          </select>
        </div>
        <div>
          <label>Asignado a <span className="req">*</span></label>
          <select value={form.assigned_to} onChange={(e) => set('assigned_to', e.target.value)}>
            {users.map((u) => <option key={u.email} value={u.email}>{nameByEmail(u.email, users)}</option>)}
          </select>
        </div>

        {/* ¿Qué es? */}
        <div className="form-section">Qué es</div>
        <div className={errField === 'categoria' ? 'field-error' : ''}>
          <label>Categoría <span className="req">*</span></label>
          <select value={form.categoria} onChange={(e) => set('categoria', e.target.value)}>
            <option value="">—</option>
            {(dimensions.categoria || []).map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <label>Pieza</label>
          <select value={form.pieza} disabled={!form.categoria} onChange={(e) => set('pieza', e.target.value)}>
            <option value="">—</option>
            {piezas.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <label>Urgencia</label>
          <Seg value={form.urgencia} options={URGENCIAS} onChange={(v) => set('urgencia', v)} colorize />
        </div>
        <div>
          <label>Tipo</label>
          <Seg value={form.tipo} options={TIPOS} onChange={(v) => set('tipo', v)} />
        </div>

        {/* ¿Cuándo? */}
        <div className="form-section">Cuándo</div>
        <div>
          <label>Fecha de solicitud</label>
          <input type="date" value={form.fecha_solicitud} onChange={(e) => set('fecha_solicitud', e.target.value)} />
          <span className="hint">El pedido formal (por defecto hoy).</span>
        </div>
        <div className={errField === 'deadline' ? 'field-error' : ''}>
          <label>Entrega solicitada <span className="req">*</span></label>
          <input type="date" value={form.deadline} onChange={(e) => set('deadline', e.target.value)} />
        </div>

        {/* Más detalles */}
        <button type="button" className="ghost" style={{ alignSelf: 'flex-start' }} aria-expanded={details} onClick={toggleDetails}>
          {details ? '▾ Menos detalles' : '▸ Más detalles'}
        </button>

        {details && (
          <div className="grid" style={{ gap: 'var(--sp-5)' }}>
            <div className="form-section">Clasificación</div>
            <div>
              <label>Proyecto</label>
              <select value={form.proyecto} onChange={(e) => setProyecto(e.target.value)}>
                <option value="">—</option>
                {(dimensions.proyecto || []).map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label>Dirección {form.proyecto && <span className="hint" style={{ display: 'inline', marginLeft: 4 }}>(del proyecto)</span>}</label>
              <select value={form.direccion} disabled={!!form.proyecto} onChange={(e) => set('direccion', e.target.value)}>
                <option value="">—</option>
                {(dimensions.direccion || []).map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </div>
            {dimSelect('sede', 'Sede', dimensions.sede || [])}

            <div className="form-section">Esfuerzo</div>
            <div>
              <label>Dificultad</label>
              <Seg value={form.dificultad || ''} options={DIFICULTADES} onChange={(v) => set('dificultad', form.dificultad === v ? '' : v)} />
            </div>
            <div>
              <label>Horas estimadas</label>
              <input type="number" min={0} step={0.5} value={form.horas_estimadas} onChange={(e) => set('horas_estimadas', e.target.value)} />
            </div>
            <div>
              <label>Inicio</label>
              <input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} />
            </div>

            <div className="form-section">Relación</div>
            <div>
              <label>¿Es ajuste / devolución de otra tarea?</label>
              <AjusteInput value={form.ajuste} onChange={(v) => set('ajuste', v)} tasks={tareas} users={users} />
              <span className="hint">Escribí una nota libre o elegí una tarea existente como referencia.</span>
            </div>
          </div>
        )}

        {err && <div className="error">{err}</div>}

        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, cursor: 'pointer' }}>
            <input type="checkbox" checked={keepGoing} onChange={toggleKeep} style={{ width: 'auto' }} />
            Crear y seguir cargando
          </label>
          <div className="row" style={{ alignItems: 'center' }}>
            <span className="hint" style={{ marginTop: 0 }}>⌘/Ctrl + Enter</span>
            <button onClick={() => setForm(fresh(form.assigned_to))}>Limpiar</button>
            <button className="primary" disabled={saving} onClick={submit}>{saving ? 'Creando…' : 'Crear tarea'}</button>
          </div>
        </div>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
